import React from 'react';
import RmaQueue from './RmaQueue';
import RmaQueueStaging from './RmaQueueStaging';
import RmaDetail from './RmaDetail';
import RmaNew from './RmaNew';
import RmaReceive from './RmaReceive';
import { stageOf, isOverdue, stageAgeDays, daysOverSla, warrantyEnd, wasInWarrantyAt,
         RMA_STAGE, RMA_OUTCOME } from './rmaHelpers';
import { downloadCsv } from '../../utils/csv';

// The RMA module's sub-router. App.js renders exactly one <RmaModule/> for view==="rma";
// everything below the nav is owned here.
//
// This owns the only navigation state, replacing the old implicit sub-view flag
// (rmaReturnTestShipmentId, which doubled as "which shipment" and "show the wizard").
export default function RmaModule({
  st, setSt, audit, account, isReadOnly, isAdmin,
  darkMode, OW, DM, ss, lb, IC, RC,
  getPF, getQCDefs, getQCPF,
  FilterSelect, SegmentedControl, DatePicker, Pill,
  createRmaShipment, updateRmaShipment, dispatchRmaShipment, chaseRmaShipment, receiveRmaUnits,
  closeRmaShipment, removeDeviceFromRma, printRmaShipmentReport,
  prefill, clearPrefill,
  onOpenDevice, legacyReturnTest,
  buzzSuccess, buzzError,
}) {
  const [screen, setScreen] = React.useState('queue');
  const [selectedId, setSelectedId] = React.useState(null);
  // Device ids handed to the New RMA screen when the operator stages units from the queue's
  // QC / quarantine sections. Consumed and cleared by RmaNew on mount.
  const [pendingIds, setPendingIds] = React.useState(null);
  // Recomputed once per mount rather than per render, so a row's "days in stage" cannot
  // change mid-interaction.
  const now = React.useMemo(() => Date.now(), []);

  // Memoised so the `|| []` fallback cannot produce a fresh array identity on every render
  // and invalidate every downstream useMemo.
  const shipments = React.useMemo(() => st.rmaShipments || [], [st.rmaShipments]);
  const devicesById = React.useMemo(
    () => new Map((st.devices || []).map(d => [d.id, d])),
    [st.devices]
  );
  const selected = React.useMemo(
    () => shipments.find(sh => sh.id === selectedId) || null,
    [shipments, selectedId]
  );

  // A read-only user must never reach a mutating screen, even via a stale prefill.
  const go = (next, id) => {
    if (isReadOnly && (next === 'new' || next === 'receive')) return;
    setSelectedId(id ?? null);
    setScreen(next);
  };

  // ── Deep-link from elsewhere in the app (the maintenance warranty prompt) ──
  React.useEffect(() => {
    if (!prefill) return;
    if (isReadOnly) { clearPrefill && clearPrefill(); return; }
    setScreen('new');
  }, [prefill, isReadOnly, clearPrefill]);

  // ── Staging: QC failures not yet on an RMA, grouped by the PO batch they arrived on ──
  // The join is: device is Pending RMA, its QC result was a fail, and no RMA record holds
  // it. finishQCImport no longer creates a shipment, so this is what "staged" means now.
  const onRmaIds = React.useMemo(() => {
    const s = new Set();
    shipments.forEach(sh => (sh.deviceIds || []).forEach(id => s.add(id)));
    return s;
  }, [shipments]);

  const qcStaged = React.useMemo(() => {
    const staged = (st.devices || []).filter(d =>
      d.status === 'Pending RMA' && d.qc?.result === 'fail' && !onRmaIds.has(d.id));
    const byBatch = new Map();
    staged.forEach(d => {
      const key = d.importBatch || 'No batch';
      if (!byBatch.has(key)) byBatch.set(key, []);
      byBatch.get(key).push(d);
    });
    const sessions = st.qcSessions || [];
    return [...byBatch.entries()].map(([batchNo, devices]) => {
      const batchSessions = sessions.filter(s => s.importBatch === batchNo);
      const checked = batchSessions.reduce((n, s) => n + (s.devices || []).length, 0);
      const failed = batchSessions.reduce((n, s) => n + (s.devices || []).filter(x => x.result === 'fail').length, 0);
      const last = batchSessions.map(s => s.finishedAt).filter(Boolean).sort().pop();
      const meta = [
        'Goods-inwards QC',
        last ? new Date(last).toLocaleDateString('en-GB') : null,
        checked ? checked + ' checked' : null,
        failed ? failed + ' failed' : null,
      ].filter(Boolean).join(' · ');
      return {
        batchNo, devices, meta,
        types: [...new Set(devices.map(d => d.type).filter(Boolean))],
      };
    }).sort((a, b) => a.batchNo.localeCompare(b.batchNo));
  }, [st.devices, st.qcSessions, onRmaIds]);

  // Fault vocabulary: derived from the QC checklist labels, unioned with any categories
  // operators have added. The design's categories (Buttons, Charge port, Powers on,
  // Housing condition…) are already QC_DEFS labels, so deriving them keeps one vocabulary
  // instead of two, and lets the New screen's suggestion banner match the
  // quarantineFailedChecks that QC writes.
  // The New screen types faults freehand; this list is only offered as autocomplete, so
  // repeat faults reach for the same words and still group in reporting. `custom` marks
  // operator-added labels apart from the QC checklist's own vocabulary.
  const faultCategories = React.useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (label, custom) => {
      const l = String(label || '').trim();
      if (!l || seen.has(l.toLowerCase())) return;
      seen.add(l.toLowerCase());
      out.push({ label: l, custom });
    };
    (st.deviceTypes || []).forEach(t => (getQCDefs ? getQCDefs(t) : []).forEach(c => push(c.label, false)));
    (st.rmaFaultCategories || []).forEach(l => push(l, true));
    return out;
  }, [st.deviceTypes, st.rmaFaultCategories, getQCDefs]);

  // ── CSV export ──
  const exportQueue = (rows) => {
    const header = ['Ref', 'Stage', 'Units', 'Serials', 'Fault category', 'Raised', 'Raised by',
                    'Carrier', 'Tracking', 'Dispatched', 'Days in stage', 'Over SLA (days)',
                    'Expected back', 'Closed'];
    const body = rows.map(sh => {
      const serials = (sh.deviceIds || []).map(id => devicesById.get(id)?.serial || id);
      return [
        sh.ref || sh.id,
        RMA_STAGE[stageOf(sh)]?.label || stageOf(sh),
        serials.length,
        serials.join(' '),
        sh.faultMode === 'per_device' ? 'Per device' : (sh.faultCategory || ''),
        sh.createdAt ? new Date(sh.createdAt).toLocaleDateString('en-GB') : '',
        sh.createdBy || '',
        sh.carrier || '',
        sh.trackingNumber || '',
        sh.dispatchedAt ? new Date(sh.dispatchedAt).toLocaleDateString('en-GB') : '',
        stageAgeDays(sh, now),
        isOverdue(sh, now, st.rmaSettings) ? daysOverSla(sh, now, st.rmaSettings) : 0,
        sh.expectedBackAt ? new Date(sh.expectedBackAt).toLocaleDateString('en-GB') : '',
        sh.closedAt ? new Date(sh.closedAt).toLocaleDateString('en-GB') : '',
      ];
    });
    downloadCsv([header, ...body], 'rma-queue');
  };

  const exportDetail = (sh) => {
    const months = st.rmaSettings?.warrantyMonths;
    const header = ['Ref', 'Serial', 'Type', 'LB id', 'Fault category', 'Reported fault',
                    'Purchase date', 'Warranty expiry', 'In warranty at dispatch',
                    'Outcome', 'New serial', 'Destination', 'Bench tested', 'Received'];
    const body = (sh.deviceIds || []).map(id => {
      const d = devicesById.get(id);
      const o = (sh.outcomes || {})[id];
      const def = o && RMA_OUTCOME[o.outcome];
      const end = warrantyEnd(d?.purchaseDate, months);
      return [
        sh.ref || sh.id,
        d?.serial || id,
        d?.type || '',
        d?.deviceId || '',
        sh.faultMode === 'per_device' ? (sh.faultCategories?.[id] || '') : (sh.faultCategory || ''),
        (sh.faultDescriptions || {})[id] || '',
        d?.purchaseDate || '',
        end ? end.toLocaleDateString('en-GB') : '',
        d?.purchaseDate ? (wasInWarrantyAt(d.purchaseDate, sh.dispatchedAt, months) ? 'Yes' : 'No') : 'Unknown',
        def?.label || '',
        o?.newSerial || '',
        def?.destLabel || '',
        def?.needsBenchTest ? (o?.benchTested ? 'Yes' : 'No') : 'n/a',
        o?.receivedAt ? new Date(o.receivedAt).toLocaleDateString('en-GB') : '',
      ];
    });
    downloadCsv([header, ...body], (sh.ref || 'rma').toLowerCase());
  };

  // ── Screens ──
  if (screen === 'queue') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <RmaQueue
          st={st}
          shipments={shipments}
          devicesById={devicesById}
          isReadOnly={isReadOnly}
          onOpen={id => go('detail', id)}
          onNew={() => go('new')}
          onReceive={() => go('receive')}
          onExport={exportQueue}
          FilterSelect={FilterSelect}
          SegmentedControl={SegmentedControl}
          darkMode={darkMode} OW={OW} DM={DM}
          now={now}
        />
        <RmaQueueStaging
          qcStaged={qcStaged}
          isReadOnly={isReadOnly}
          onAddDevices={(ids, meta) => {
            if (isReadOnly) return;
            setSelectedId(null);
            setScreen('new');
            setPendingIds({ ids, meta });
          }}
        />
      </div>
    );
  }

  if (screen === 'detail') {
    // A shipment can vanish under us (another tab closing it, or a stale id) — fall back to
    // the queue rather than rendering a broken page.
    if (!selected) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 720 }}>
          <button onClick={() => go('queue')} className="btn btn-ghost" style={{ alignSelf: 'flex-start' }}>‹ All RMAs</button>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>That RMA no longer exists.</div>
        </div>
      );
    }
    return (
      <RmaDetail
        shipment={selected}
        devicesById={devicesById}
        st={st}
        isReadOnly={isReadOnly}
        onBack={() => go('queue')}
        // The detail screen collects carrier/tracking/date in its own inline panel and
        // hands them down, so an RMA saved without a reference can be given one at the
        // moment it actually ships.
        onDispatch={(fields) => dispatchRmaShipment(selected.id, {
          carrier: fields?.carrier ?? selected.carrier ?? '',
          trackingNumber: fields?.trackingNumber ?? selected.trackingNumber ?? '',
          dispatchedAt: fields?.dispatchedAt || undefined,
        })}
        onChase={chaseRmaShipment}
        onReceive={() => go('receive', selected.id)}
        onBenchTest={id => { if (legacyReturnTest) legacyReturnTest(id); }}
        onClose={closeRmaShipment}
        // removeDeviceFromRma was threaded this far but never reached a screen, so pulling
        // a wrongly-scanned unit off a raised RMA was impossible without deleting the RMA.
        onRemoveDevice={removeDeviceFromRma}
        onEdit={() => go('edit', selected.id)}
        onExportCsv={() => exportDetail(selected)}
        onPrint={() => printRmaShipmentReport(selected.id)}
        FilterSelect={FilterSelect}
        DatePicker={DatePicker}
        darkMode={darkMode} OW={OW} DM={DM}
        now={now}
      />
    );
  }

  // Editing a raised-but-not-dispatched RMA reuses the New screen rather than growing a
  // second editor. Everything the wizard collects is exactly what an undispatched RMA is
  // made of, so "Save without dispatching" means park it here and come back — the units,
  // the fault and the shipment fields all stay adjustable until it actually ships.
  if (screen === 'edit') {
    if (!selected || stageOf(selected) !== 'awaiting_dispatch') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 720 }}>
          <button onClick={() => go('queue')} className="btn btn-ghost" style={{ alignSelf: 'flex-start' }}>‹ All RMAs</button>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            That RMA can no longer be edited — it has already been dispatched.
          </div>
        </div>
      );
    }
    return (
      <RmaNew
        key={selected.id}
        st={st}
        faultCategories={faultCategories}
        isReadOnly={isReadOnly}
        editing={selected}
        initialDeviceIds={selected.deviceIds || []}
        initialFaultCategory={selected.faultCategory || ''}
        initialFaultText={Object.values(selected.faultDescriptions || {})[0] || ''}
        onBack={() => go('detail', selected.id)}
        onCreate={(payload) => {
          const saved = updateRmaShipment(selected.id, payload);
          if (saved?.id) go('detail', saved.id); else go('queue');
        }}
        FilterSelect={FilterSelect}
        DatePicker={DatePicker}
        darkMode={darkMode} OW={OW} DM={DM}
        buzzSuccess={buzzSuccess} buzzError={buzzError}
        now={now}
      />
    );
  }

  if (screen === 'new') {
    // Ids can arrive two ways: staged from the queue's QC/quarantine sections, or deep
    // linked from the maintenance warranty prompt elsewhere in the app.
    const seedIds = pendingIds?.ids || prefill?.deviceIds || [];
    return (
      <RmaNew
        st={st}
        faultCategories={faultCategories}
        isReadOnly={isReadOnly}
        initialDeviceIds={seedIds}
        initialFaultCategory={prefill?.faultCategory || pendingIds?.meta?.faultCategory || ''}
        initialFaultText={prefill?.faultDescription || ''}
        onBack={() => { setPendingIds(null); clearPrefill && clearPrefill(); go('queue'); }}
        onCreate={(payload) => {
          const created = createRmaShipment({
            ...payload,
            source: pendingIds?.meta?.batchNo ? 'qc' : 'quarantine',
            importBatch: pendingIds?.meta?.batchNo || null,
          });
          setPendingIds(null);
          clearPrefill && clearPrefill();
          // Land on the new RMA so the operator can see what they just raised.
          if (created?.id) go('detail', created.id); else go('queue');
        }}
        FilterSelect={FilterSelect}
        DatePicker={DatePicker}
        darkMode={darkMode} OW={OW} DM={DM}
        buzzSuccess={buzzSuccess} buzzError={buzzError}
        now={now}
      />
    );
  }

  if (screen === 'receive') {
    return (
      <RmaReceive
        st={st}
        shipments={shipments}
        devicesById={devicesById}
        isReadOnly={isReadOnly}
        focusShipmentId={selectedId}
        onBack={() => go('queue')}
        onBookIn={receiveRmaUnits}
        onBenchTest={id => { if (legacyReturnTest) legacyReturnTest(id); }}
        buzzSuccess={buzzSuccess} buzzError={buzzError}
        now={now}
      />
    );
  }

  return null;
}
